import type {
  AuthObservabilityService,
  AuthService,
  ClientAppUserResult,
  ServiceForgotPasswordResult,
  ServiceSignInResult,
  ServiceSignUpResult,
  ServiceValidationResult,
  SignUpProps,
  SocialProvider,
  TranslationPort,
  User,
  ValidateUserRequest,
} from '../../types';
import type { CognitoCode } from '../../types/cognito-code';
import type { IdentityProvider } from '../identity-provider';
import type { AuthLogger } from '../../shared/auth-logger';
import type { CognitoConfig } from '../cognito-config';
import { deserializeUser } from '../session-persistence';
import { COGNITO_FEATURES } from './features';
import { AmplifyIdentityProvider } from '../amplify/provider';
import { cognitoSignIn } from './sign-in';
import { createSignUpFlow } from './sign-up';
import { cognitoForgotPassword, cognitoConfirmForgotPassword, cognitoChangePassword } from './password';
import { cognitoSignInWithSocial, cognitoAuthenticateAfterRedirect } from './social';
import { cognitoRefreshSession, cognitoSignOut, cognitoSubmitPhone } from './session';
import { fetchClientAppUserStatus } from './validate';
import { cognitoDeleteAccount } from './delete-account';
import { cognitoUpdateProfile, cognitoUpdateEmail, cognitoConfirmEmailUpdate } from './account-management';
import { ProbeStatus, AuthFlow, IdpSignInStep } from '../../constants';
import { IdpErrorName } from './errors';
import type { HttpClient } from '../http';

interface CognitoAuthServiceDeps {
  http: HttpClient;
  clientAppHttp: HttpClient;
  translation: TranslationPort;
  logger?: AuthLogger;
  cognitoConfig: CognitoConfig;
  amplifyConfigured?: boolean;
  observability?: AuthObservabilityService;
  /** Theme name (e.g. "exprealtyCA") — sent as clientMetadata.tenant on every Cognito SDK call. */
  tenant: string;
}

interface LoginResponse {
  data: {
    id: string | number;
    attributes: Record<string, unknown>;
  };
}

/**
 * Cognito-backed implementation of {@link AuthService}.
 *
 * Orchestrates password and social sign-in, sign-up with OTP confirmation,
 * password recovery, session management, and user validation against both
 * the client-app DB (`next_steps`) and the Cognito user pool (SRP probe).
 *
 * User validation is driven by a pure reducer
 * ({@link validationReducer}) — this class acts as the side-effect shell.
 */
export class CognitoAuthService implements AuthService {
  private idp: IdentityProvider;
  private http: HttpClient;
  private clientAppHttp: HttpClient;
  private t: TranslationPort['t'];
  private logger?: AuthLogger;
  private observability?: AuthObservabilityService;
  private signUpFlow: ReturnType<typeof createSignUpFlow>;
  private amplifyConfigured: boolean;
  private readonly cognitoConfig: CognitoConfig;

  readonly supportedFeatures = COGNITO_FEATURES;

  constructor(deps: CognitoAuthServiceDeps) {
    this.cognitoConfig = deps.cognitoConfig;
    this.amplifyConfigured = deps.amplifyConfigured ?? false;
    this.idp = new AmplifyIdentityProvider(deps.cognitoConfig, deps.tenant);
    this.http = deps.http;
    this.clientAppHttp = deps.clientAppHttp;
    this.t = deps.translation.t;
    this.logger = deps.logger;
    this.observability = deps.observability;
    this.signUpFlow = createSignUpFlow(this.idp, this.http, this.t, this.logger);
  }

  async checkClientAppUser(email: string): Promise<ClientAppUserResult> {
    try {
      const result = await fetchClientAppUserStatus(this.clientAppHttp, { email });
      return { exists: result.isExisting, firstName: result.firstName, authMethods: result.authMethods };
    } catch (err) {
      this.observability?.dispatch({ type: 'email_lookup_failure', error: err });
      return { exists: false, firstName: '', authMethods: []};
    }
  }

  async checkCognitoUser(email: string): Promise<CognitoCode> {
    try {
      const probe = await this.idp.probeUserStatus(email);
      switch (probe) {
        case ProbeStatus.RequiresPasswordReset: return 'PasswordResetRequiredException';
        case ProbeStatus.RequiresEmailVerification: return 'UserNotConfirmedException';
        case ProbeStatus.UserNotFound: return 'UserNotFoundException';
        case ProbeStatus.AccountReady: return 'AccountReady';
        case ProbeStatus.Unknown:
          // Probe could not determine status (network error, unexpected response).
          // Fall through to AccountReady so the sign-in attempt surfaces the real
          // error from Cognito rather than incorrectly telling the user they don't exist.
          this.observability?.dispatch({ type: 'idp_probe_failure', error: new Error('Cognito probe returned unknown status') });
          return 'AccountReady';
      }
    } catch (err) {
      // Probe threw unexpectedly. Fall through to AccountReady for the same
      // reason as ProbeStatus.Unknown above.
      this.observability?.dispatch({ type: 'idp_probe_failure', error: err });
      return 'AccountReady';
    }
  }

  async signIn(email: string, password: string): Promise<ServiceSignInResult> {
    try {
      const result = await cognitoSignIn(this.idp, this.http, email, password, undefined, this.t, this.logger);
      if (result.needsPhone) return { needsPhone: true };
      return { user: result.user };
    } catch (err) {
      this.observability?.dispatch({ type: 'signin_failure', error: err });
      throw err;
    }
  }

  async signUp(props: SignUpProps): Promise<ServiceSignUpResult> {
    try {
      return await this.signUpFlow.signUp(props);
    } catch (err) {
      this.observability?.dispatch({ type: 'register_failure', error: err });
      throw err;
    }
  }

  async confirmSignUp(email: string, code: string): Promise<ServiceSignUpResult> {
    try {
      return await this.signUpFlow.confirmSignUp(email, code);
    } catch (err) {
      this.observability?.dispatch({ type: 'register_failure', error: err });
      throw err;
    }
  }

  async resendSignUpCode(email: string): Promise<void> {
    try {
      return await this.signUpFlow.resendSignUpCode(email);
    } catch (err) {
      this.observability?.dispatch({ type: 'register_failure', error: err });
      throw err;
    }
  }

  async signOut(redirectUrl?: string): Promise<void> {
    try {
      return await cognitoSignOut(this.idp, this.clientAppHttp, this.logger, redirectUrl);
    } catch (err) {
      this.observability?.dispatch({ type: 'signout_failure', error: err });
      throw err;
    }
  }

  async validateUser(request: ValidateUserRequest): Promise<ServiceValidationResult> {
    const empty: ServiceValidationResult = { isExisting: false, authMethods: [], firstName: '', themeData: {}};

    let clientAppResult: ServiceValidationResult | null = null;
    try {
      clientAppResult = await fetchClientAppUserStatus(this.clientAppHttp, request);
    } catch (err) {
      this.observability?.dispatch({ type: 'email_lookup_failure', error: err });
    }

    if (request.email) {
      try {
        const probe = await this.idp.probeUserStatus(request.email);
        const meta = clientAppResult ?? empty;
        switch (probe) {
          case ProbeStatus.RequiresPasswordReset:
            return { ...meta, isExisting: true, authMethods: meta.authMethods.length ? meta.authMethods : ['password'], authFlow: AuthFlow.RequiresPasswordReset };
          case ProbeStatus.RequiresEmailVerification:
            return { ...meta, isExisting: true, authFlow: AuthFlow.RequiresEmailVerification };
          case ProbeStatus.UserNotFound:
            return { ...meta, isExisting: false };
          case ProbeStatus.AccountReady:
            return { ...meta, isExisting: true, authMethods: meta.authMethods.length ? meta.authMethods : ['password']};
          case ProbeStatus.Unknown:
            this.observability?.dispatch({ type: 'idp_probe_failure', error: new Error('Cognito probe returned unknown status') });
            break;
        }
      } catch (err) {
        this.observability?.dispatch({ type: 'idp_probe_failure', error: err });
      }
    }

    if (clientAppResult) return clientAppResult;
    // Both the client-app API and the Cognito probe failed. Return the empty
    // "unknown user" result so the caller can present the registration form
    // rather than surfacing an unrecoverable error to the user.
    return empty;
  }

  async idpRedirectSignIn(provider: SocialProvider, customState?: string): Promise<void> {
    if (!this.amplifyConfigured) {
      throw new Error(
        'Cognito is not configured (missing COGNITO_USER_POOL_ID / COGNITO_CLIENT_ID). ' +
        'Social sign-in is unavailable.'
      );
    }
    try {
      return await cognitoSignInWithSocial(this.idp, provider, customState);
    } catch (err) {
      const name = (err as { name?: string } | null)?.name;
      if (name !== IdpErrorName.UserAlreadyAuthenticated) throw err;
      this.logger?.log(IdpErrorName.UserAlreadyAuthenticated, { provider });
      await this.idp.signOutLocally();
      return cognitoSignInWithSocial(this.idp, provider, customState);
    }
  }

  /** @deprecated Not supported on Cognito — social sign-in is always via OAuth redirect. */
  async legacySocialSignIn(): Promise<never> {
    throw new Error('legacySocialSignIn is not supported by CognitoAuthService. Use idpRedirectSignIn instead.');
  }

  async authenticateAfterRedirect(provider: SocialProvider): Promise<ServiceSignInResult> {
    try {
      const result = await cognitoAuthenticateAfterRedirect(this.idp, this.http, provider, this.logger);
      // Make sure to return the profile data provided by the social provider
      if (result.needsPhone) {
        const profile = 'profile' in result ? result.profile : undefined;
        return { needsPhone: true, profile };
      }
      return { user: result.user };
    } catch (err) {
      this.observability?.dispatch({ type: 'signin_failure', error: err, authMethod: provider });
      throw err;
    }
  }

  async forgotPassword(email: string, _theme: string): Promise<ServiceForgotPasswordResult> {
    try {
      const result = await cognitoForgotPassword(this.idp, email, this.t);
      if (result.error) {
        this.observability?.dispatch({ type: 'signin_failure', error: new Error(result.error) });
      }
      return result;
    } catch (err) {
      this.observability?.dispatch({ type: 'signin_failure', error: err });
      throw err;
    }
  }

  async confirmForgotPassword(email: string, code: string, password: string, _theme: string): Promise<ServiceForgotPasswordResult> {
    try {
      const result = await cognitoConfirmForgotPassword(this.idp, email, code, password, this.t);
      if (result.error) {
        this.observability?.dispatch({ type: 'signin_failure', error: new Error(result.error) });
      }
      return result;
    } catch (err) {
      this.observability?.dispatch({ type: 'signin_failure', error: err });
      throw err;
    }
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<ServiceForgotPasswordResult> {
    try {
      const result = await cognitoChangePassword(this.idp, currentPassword, newPassword, this.t);
      if (result.error) {
        this.observability?.dispatch({ type: 'change_password_failure', error: new Error(result.error) });
      }
      return result;
    } catch (err) {
      this.observability?.dispatch({ type: 'change_password_failure', error: err });
      throw err;
    }
  }

  async confirmUser(token: string, theme: string): Promise<ServiceSignInResult> {
    try {
      const response = await this.clientAppHttp.request<LoginResponse>(
        '/services/api/v3/users/confirmation',
        'GET',
        { confirmationToken: token, theme }
      );
      return { user: deserializeUser({ id: response.data.id, ...response.data.attributes }) };
    } catch (err) {
      this.observability?.dispatch({ type: 'signin_failure', error: err });
      throw err;
    }
  }

  async refreshSession(): Promise<ServiceSignInResult | null> {
    return cognitoRefreshSession(this.idp, this.http, this.logger);
  }

  async getSignInProvider(): Promise<string | null> {
    return this.idp.getSignInProvider(this.logger);
  }

  getLogoutUrl(_redirectUri: string): string | null {
    const domain = this.cognitoConfig.domain.replace(/\/$/, '');
    const params = new URLSearchParams({
      client_id: this.cognitoConfig.clientId,
      logout_uri: this.cognitoConfig.redirectSignOut,
    });
    return `${domain}/logout?${params.toString()}`;
  }

  async clearStaleSession(): Promise<void> {
    try {
      await this.idp.signOutLocally();
    } catch (err) {
      this.logger?.log(IdpSignInStep.ClearStaleSessionError, { error: err instanceof Error ? err.message : String(err) });
      this.observability?.dispatch({ type: 'clear_stale_session_failure', error: err });
    }
  }

  async deleteAccount(): Promise<void> {
    try {
      await cognitoDeleteAccount(this.idp, this.http, this.logger);
    } catch (err) {
      this.observability?.dispatch({ type: 'delete_account_failure', error: err });
      throw err;
    }
  }

  async updateProfile(params: User): Promise<{ success: true } | { error: string }> {
    try {
      return await cognitoUpdateProfile(this.idp, this.http, params);
    } catch (err) {
      this.observability?.dispatch({ type: 'update_profile_failure', error: err });
      return { error: this.t('errors.profile_update_failed', { defaultValue: 'Failed to update profile. Please try again.' }) };
    }
  }

  async updateEmail(currentUser: User, newEmail: string, retypeEmail: string, password?: string, passwordConfirmation?: string): Promise<{ success: true } | { error: string }> {
    try {
      return await cognitoUpdateEmail(this.idp, this.http, currentUser, newEmail, retypeEmail, password, passwordConfirmation);
    } catch (err) {
      this.observability?.dispatch({ type: 'update_email_failure', error: err });
      return { error: this.t('errors.email_update_failed', { defaultValue: 'Failed to update email. Please try again.' }) };
    }
  }

  async confirmEmailUpdate(code: string): Promise<{ success: true } | { error: string }> {
    try {
      return await cognitoConfirmEmailUpdate(this.idp, code);
    } catch (err) {
      this.observability?.dispatch({ type: 'update_email_confirmation_failure', error: err });
      return { error: this.t('errors.email_confirm_failed', { defaultValue: 'Invalid or expired verification code.' }) };
    }
  }

  onOAuthRedirectComplete(onSuccess: () => void, onError: (error: string) => void): () => void {
    return this.idp.onOAuthRedirectComplete!(onSuccess, onError);
  }

  async submitPhone(_userId: string | number, phone: string, profile?: { firstName: string; lastName: string }): Promise<ServiceSignInResult> {
    try {
      return await cognitoSubmitPhone(this.idp, this.http, phone, profile, this.logger);
    } catch (err) {
      this.observability?.dispatch({ type: 'submit_phone_failure', error: err });
      throw err;
    }
  }
}
